import { useState, useCallback, useRef } from 'react';
import { getViewportBBox, computeVisibleLabels } from './graphUtils';
import type { BBox, LodTier, NodeId, GraphNodeRenderable } from '../../types/graph';

export const LOD_TIER = {
  OVERVIEW: 'overview' as const,
  NAVIGATION: 'navigation' as const,
  DETAIL: 'detail' as const,
};

function getTier(scale: number): LodTier {
  if (scale < 1.5) return LOD_TIER.OVERVIEW;
  if (scale <= 4.0) return LOD_TIER.NAVIGATION;
  return LOD_TIER.DETAIL;
}

/**
 * Hook for zoom-dependent adaptive rendering.
 * Tracks globalScale, computes LOD tier, and provides viewport bbox.
 */
export function useAdaptiveRendering(graphRef: React.RefObject<any>) {
  const [globalScale, setGlobalScale] = useState(1);
  const [lodTier, setLodTier] = useState<LodTier>(LOD_TIER.OVERVIEW);
  const bboxRef = useRef<BBox>({ left: -Infinity, top: -Infinity, right: Infinity, bottom: Infinity });
  const visibleLabelsRef = useRef<Set<NodeId> | null>(null);
  const nodesRef = useRef<GraphNodeRenderable[]>([]);
  const adjacencyMapRef = useRef<Map<NodeId, Set<NodeId>>>(new Map());

  const setContext = useCallback((nodes: GraphNodeRenderable[], adjacencyMap: Map<NodeId, Set<NodeId>>) => {
    nodesRef.current = nodes;
    adjacencyMapRef.current = adjacencyMap;
  }, []);

  const handleZoom = useCallback(({ k }: { k: number }) => {
    setGlobalScale(k);
    setLodTier(getTier(k));
    const bbox = getViewportBBox(graphRef);
    if (bbox) {
      bboxRef.current = bbox;
      visibleLabelsRef.current = computeVisibleLabels(
        nodesRef.current, adjacencyMapRef.current, k, bbox
      );
    }
  }, [graphRef]);

  const getBBox = useCallback((): BBox => {
    const bbox = getViewportBBox(graphRef);
    if (bbox) bboxRef.current = bbox;
    return bboxRef.current;
  }, [graphRef]);

  const getVisibleLabels = useCallback((): Set<NodeId> | null => visibleLabelsRef.current, []);

  return {
    globalScale,
    lodTier,
    handleZoom,
    getBBox,
    getVisibleLabels,
    setContext,
  };
}
